// apps/web/src/features/cargo/forms/useCargoFormOptions.ts
//
// Builds the Select option lists the add/edit modals share (#1232): the stored
// status (exactly the two stored values — "issued" is derived, never stored),
// the carrier-payment status (paid / not_paid only), and the carrier /
// path-format options. The fetches are gated behind `enabled` so the queries
// fire only while a modal is open.
//
// WHY the SYSTEM lists, not the cargo-local dictionaries (#1947, stage 3 of the
// carrier/road unification): the write side now validates carrier against the
// active `Carrier` catalog and pathFormat against active `RoadOption`, so the
// picker must offer exactly those values — both the create AND the edit modal,
// because create is validated too.
//
// WHY the current stored value is appended when missing: a row may carry a
// legacy carrier/path string the system list has since archived. Dropping it
// from the picker would silently rewrite the field on save, so the archived
// value stays selectable for this row.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CARGO_CARRIER_PAYMENT_STATUSES,
  CARGO_CARRIER_PAYMENT_STATUS_LABELS,
  CARGO_STATUSES,
  CARGO_STATUS_LABELS,
  type CargoCarrierPaymentStatus,
  type CargoStatus,
  type GetCarriersResponse,
} from '@magimp/shared';
import type { SelectOption } from '../../../ui/index.js';

import { listCarriers } from '../../../api/requests/index.js';
import { useActiveRoadOptions } from '../../requests/dictionarySelections.queries.js';
import type { CargoManagerOption } from '../components/CargoFiltersPanel.js';

// Active system carriers for the form picker (#1947). `limit: 100` is the
// `/carriers` schema max; a single page covers the catalog the cargo modals
// offer. Read from the same `/carriers?active=true` surface the carriers admin
// uses, so the picker and the write-side validation share one source of truth.
const CARGO_FORM_ACTIVE_CARRIERS_QK = ['cargo', 'form', 'active-carriers'] as const;

function useActiveCarrierOptions(enabled: boolean) {
  return useQuery<GetCarriersResponse>({
    queryKey: CARGO_FORM_ACTIVE_CARRIERS_QK,
    queryFn: () => listCarriers({ active: true, limit: 100 }),
    enabled,
  });
}

export type { CargoManagerOption };

/** Stored statuses only — `in_transit` / `arrived_rf` (no derived "issued"). */
export function cargoStatusOptions(): SelectOption[] {
  return CARGO_STATUSES.map((s: CargoStatus) => ({ value: s, label: CARGO_STATUS_LABELS[s] }));
}

/**
 * 3-state edit-modal status options: the two stored statuses plus the derived
 * "issued" option. The caller supplies the translated label for `issued` because
 * this module has no i18n dependency.
 */
export function cargoEditStatusOptions(issuedLabel: string): SelectOption[] {
  return [...cargoStatusOptions(), { value: 'issued', label: issuedLabel }];
}

/** Carrier-payment statuses — paid / not_paid only. */
export function cargoCarrierPaymentOptions(): SelectOption[] {
  return CARGO_CARRIER_PAYMENT_STATUSES.map((s: CargoCarrierPaymentStatus) => ({
    value: s,
    label: CARGO_CARRIER_PAYMENT_STATUS_LABELS[s],
  }));
}

function withCurrent(options: SelectOption[], current: string): SelectOption[] {
  if (current.length === 0) return options;
  if (options.some((o) => o.value === current)) return options;
  return [...options, { value: current, label: current }];
}

export interface CargoDictionaryOptions {
  carrierOptions: SelectOption[];
  pathFormatOptions: SelectOption[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Carrier + path-format pickers sourced from the ACTIVE SYSTEM lists (#1947):
 * the `Carrier` catalog (`/carriers?active=true`) and active `RoadOption`s.
 * `currentCarrier`/`currentPathFormat` keep an archived value on the row
 * selectable so saving never silently clears it.
 */
export function useCargoDictionaryOptions(
  enabled: boolean,
  currentCarrier: string,
  currentPathFormat: string,
): CargoDictionaryOptions {
  const carriers = useActiveCarrierOptions(enabled);
  const paths = useActiveRoadOptions({ enabled });

  const carrierOptions = useMemo<SelectOption[]>(() => {
    const base = (carriers.data?.items ?? []).map((o) => ({ value: o.name, label: o.name }));
    return withCurrent(base, currentCarrier);
  }, [carriers.data?.items, currentCarrier]);

  const pathFormatOptions = useMemo<SelectOption[]>(() => {
    const base = (paths.data ?? []).map((o) => ({ value: o.name, label: o.name }));
    return withCurrent(base, currentPathFormat);
  }, [paths.data, currentPathFormat]);

  return {
    carrierOptions,
    pathFormatOptions,
    isLoading: carriers.isLoading || paths.isLoading,
    isError: carriers.isError || paths.isError,
  };
}
